import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ErrorBanner } from "../../components/admin/AdminUI";

const schema = z.object({
  email: z.string().email("Ongeldig e-mailadres"),
});

/**
 * Stap 1 van "wachtwoord vergeten": vraag een herstelmail aan.
 *
 * Het scherm zegt na verzenden altijd hetzelfde, of het adres nu bestaat of niet.
 * Zou het verschil maken, dan kan iemand hier uitproberen wie er een account heeft.
 */
export default function WachtwoordVergetenPage() {
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verstuurd, setVerstuurd] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values) {
    setApiError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/admin/wachtwoord-vergeten", { email: values.email });
      setVerstuurd(true);
    } catch (e) {
      setApiError(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fulkra flex min-h-screen items-center justify-center px-4 py-12"
      style={{ background: "var(--surface-page)" }}
      data-testid="wachtwoord-vergeten-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Fulkra Works</h1>
          <p className="text-sm text-slate-600" style={{ marginTop: "var(--ruimte-4)" }}>Preventieprogramma voor staand en lopend werk</p>
        </div>
        <div
          style={{
            borderRadius: "var(--radius-vlak)", border: "1px solid var(--border-subtle)",
            background: "var(--surface-card)", padding: "var(--ruimte-9)",
            boxShadow: "var(--elevation-card)",
          }}
        >
          {verstuurd ? (
            <div data-testid="wachtwoord-vergeten-bevestiging">
              <h2 className="text-xl font-semibold text-slate-900">Kijk in uw mail</h2>
              <p className="text-sm text-slate-600" style={{ marginTop: "var(--ruimte-5)" }}>
                Als er een account bij dit e-mailadres hoort, staat er nu een bericht klaar met een
                link om een nieuw wachtwoord te kiezen. Die link werkt één keer en verloopt na een uur.
              </p>
              <p className="text-sm text-slate-600" style={{ marginTop: "var(--ruimte-5)" }}>
                Niets ontvangen? Kijk in de map ongewenste e-mail, of vraag het opnieuw aan.
              </p>
              <Link
                to="/admin/login"
                className="mt-6 inline-flex text-sm text-blue-700 underline underline-offset-4 hover:text-blue-800"
              >
                Terug naar inloggen
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-slate-900">Wachtwoord vergeten</h2>
              <p className="text-sm text-slate-500" style={{ marginTop: "var(--ruimte-3)" }}>
                Vul uw e-mailadres in; u krijgt een link om een nieuw wachtwoord te kiezen.
              </p>

              <form className="mt-6 flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)} noValidate>
                <ErrorBanner error={apiError} />
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">E-mailadres</span>
                  <input
                    type="email"
                    autoComplete="username"
                    data-testid="vergeten-email"
                    placeholder="naam@bedrijf.nl"
                    {...register("email")}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  {errors.email ? (
                    <p className="text-xs text-rose-600" data-testid="vergeten-email-error" style={{ marginTop: "var(--ruimte-3)" }}>
                      {errors.email.message}
                    </p>
                  ) : null}
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="btn-vergeten-submit"
                  className="mt-2 inline-flex justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60"
                >
                  {submitting ? "Bezig…" : "Stuur mij een link"}
                </button>
                <Link
                  to="/admin/login"
                  className="text-center text-sm text-blue-700 underline underline-offset-4 hover:text-blue-800"
                >
                  Terug naar inloggen
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
